import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import './i18n';

import ThemeProvider from '@mui/material/styles/ThemeProvider';
import CssBaseline from '@mui/material/CssBaseline/CssBaseline';
import {
  doGetAuthenticatedUserAsync,
  selectDisplayName,
  selectEmail,
  selectIsAdministrator,
  selectIsRegistered,
  doUpsertUserPlan,
  selectGuid
} from './features/logon/logonSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import navigationPaths from './engine/navigationPaths';

import ToastMessage from 'components/toastMessage';

import theme from 'oems/theme';

import getAllRoutes from 'routes';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { ErrorBoundary } from 'react-error-boundary';
import SomethingWentWrong from 'pages/SomethingWentWrong/SomethingWentWrong';
import PageNotFound from 'pages/PageNotFound/PageNotFound';
import { EmptyLayout, MainLayout } from 'components/layouts';
import { SnakeGame } from './snake'
//import { JokesGame } from './jokes'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const email = useSelector(selectEmail);
  const displayName = useSelector(selectDisplayName);
  const isRegisteredSelector = useSelector(selectIsRegistered);
  const isAdministratorSelector = useSelector(selectIsAdministrator);
  const ownerId = useSelector(selectGuid);

  const [routes, setRoutes] = useState([]);

  const [emailConfirmedMessageShown, setEmailConfirmedMessageShown] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);

  React.useEffect(() => {
    dispatch(doGetAuthenticatedUserAsync());
    const redirect = searchParams.get('newInvoice');
    if (redirect) {
      navigate(navigationPaths().InvoicesForm, { state: { mode: 'create' } });
    }

    const confirmRedirect = searchParams.get('confirmed');
    if (confirmRedirect && !emailConfirmed) {
      setEmailConfirmedMessageShown(true);
      setEmailConfirmed(true);
    }

    setRoutes(
      getAllRoutes(
        displayName?.length === 0 ? 'Nieznajomy' : displayName,
        isAdministratorSelector,
        isRegisteredSelector,
      ),
    );

    const spinner = document.getElementById('spinner');
    if (spinner) {
      spinner.style.display = 'none';
    }
  }, [email]);

  React.useEffect(() => {
    const planSelected = searchParams.get('plan');
    if(planSelected != null && planSelected != '' && ownerId != "") {
      dispatch(doUpsertUserPlan({ key: "plan_"+planSelected }));
    }
  }, [ownerId]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={
              route.noLayout ? (
                <EmptyLayout>{route.component}</EmptyLayout>
              ) : (
                <MainLayout routes={routes}>{route.component}</MainLayout>
              )
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  function Fallback({ error, resetErrorBoundary }) {
    return <SomethingWentWrong />;
  }

  return (
    routes.length > 0 && (
      <ErrorBoundary FallbackComponent={Fallback}>
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_APP_ID}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <ToastMessage
                open={emailConfirmedMessageShown}
                onClose={() => setEmailConfirmedMessageShown(false)}
                severity="info"
                message={'Twoje konto zostało potwierdzone!'}
              />
              <Routes>
                {getRoutes(routes)}
                {isRegisteredSelector && <Route path="/" element={<Navigate to={navigationPaths().Invoices} />} />}
                {!isRegisteredSelector && (
                  <Route
                    path="/"
                    element={<Navigate to={navigationPaths().InvoicesForm} state={{ mode: 'create' }} />}
                  />
                )}
                <Route path="*" element={<PageNotFound />} />
                <Route path="/snake" element={<SnakeGame />} />
                
              </Routes>
            </ThemeProvider>
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </ErrorBoundary>
    )
  );
}

export default App;
